import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import {
  Mail,
  Phone,
  Globe,
  MapPin,
  Star,
  CheckCircle,
  Play,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  Facebook,
  Twitter,
  Instagram,
  Linkedin,
  Loader2,
  User,
  DollarSign,
  Clock,
  MessageCircle,
  Quote,
  Image as ImageIcon
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const MemberPromoPage = () => {
  const { username } = useParams();
  const [promoPage, setPromoPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [galleryIndex, setGalleryIndex] = useState(0);
  const [showVideo, setShowVideo] = useState(false);

  useEffect(() => {
    fetchPromoPage();
  }, [username]);

  const fetchPromoPage = async () => {
    try {
      const response = await axios.get(`${API}/api/promo-pages/member/${username}`);
      setPromoPage(response.data);
    } catch (error) {
      if (error.response?.status === 404) {
        setPromoPage(null);
      } else {
        toast.error('Failed to load member page');
      }
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price, priceType) => {
    if (!price && priceType !== 'contact') return null;
    
    switch (priceType) {
      case 'hourly':
        return `$${price}/hr`;
      case 'starting_at':
        return `From $${price}`;
      case 'contact':
        return 'Contact for pricing';
      default:
        return `$${price}`;
    }
  };

  const renderSocialIcon = (platform) => {
    const icons = {
      facebook: Facebook,
      twitter: Twitter,
      instagram: Instagram,
      linkedin: Linkedin
    };
    const Icon = icons[platform] || Globe;
    return <Icon className="w-5 h-5" />;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!promoPage) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Card className="max-w-md text-center p-8">
          <User className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" />
          <h2 className="text-xl font-heading mb-2">Page Not Found</h2>
          <p className="text-muted-foreground mb-4">
            This member doesn't have a promotional page yet.
          </p>
          <Link to="/">
            <Button>Back to Home</Button>
          </Link>
        </Card>
      </div>
    );
  }

  const isPremium = promoPage.membership_tier === 'premium';

  return (
    <div className="min-h-screen bg-background">
      {/* Cover Image */}
      <div className="relative h-64 md:h-80 bg-gradient-to-r from-primary/20 to-primary/10">
        {promoPage.cover_image_url && (
          <img 
            src={promoPage.cover_image_url} 
            alt="Cover"
            className="w-full h-full object-cover"
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-background/80 to-transparent" />
      </div>

      <div className="container mx-auto px-4 -mt-24 relative z-10">
        {/* Profile Header */}
        <Card className="mb-8">
          <CardContent className="p-6 md:p-8">
            <div className="flex flex-col md:flex-row gap-6">
              {/* Logo/Avatar */}
              <div className="flex-shrink-0">
                {promoPage.logo_url ? (
                  <img 
                    src={promoPage.logo_url} 
                    alt={promoPage.display_name}
                    className="w-32 h-32 rounded-xl object-cover border-4 border-background shadow-lg"
                  />
                ) : (
                  <Avatar className="w-32 h-32 border-4 border-background shadow-lg">
                    <AvatarImage src={promoPage.avatar_url} />
                    <AvatarFallback className="text-3xl">
                      {promoPage.display_name?.charAt(0) || promoPage.username?.charAt(0)}
                    </AvatarFallback>
                  </Avatar>
                )}
              </div>

              {/* Info */}
              <div className="flex-1">
                <div className="flex flex-wrap items-center gap-2 mb-2">
                  <h1 className="text-2xl md:text-3xl font-heading font-medium">
                    {promoPage.display_name}
                  </h1>
                  {promoPage.is_verified && (
                    <CheckCircle className="w-6 h-6 text-blue-500" />
                  )}
                  <Badge className={isPremium ? 'bg-gradient-to-r from-amber-500 to-orange-500' : 'bg-primary'}>
                    {isPremium ? 'Premium' : 'Pro'} Member
                  </Badge>
                </div>
                
                <p className="text-muted-foreground mb-1">@{promoPage.username}</p>
                
                <h2 className="text-xl text-primary font-medium mb-2">
                  {promoPage.headline}
                </h2>
                
                {promoPage.tagline && (
                  <p className="text-muted-foreground mb-4">{promoPage.tagline}</p>
                )}

                {/* Contact Buttons */}
                <div className="flex flex-wrap gap-3">
                  {promoPage.contact_email && (
                    <Button 
                      variant="outline" 
                      onClick={() => window.location.href = `mailto:${promoPage.contact_email}`}
                    >
                      <Mail className="w-4 h-4 mr-2" />
                      Email
                    </Button>
                  )}
                  {promoPage.contact_phone && (
                    <Button 
                      variant="outline"
                      onClick={() => window.location.href = `tel:${promoPage.contact_phone}`}
                    >
                      <Phone className="w-4 h-4 mr-2" />
                      Call
                    </Button>
                  )}
                  {promoPage.website_url && (
                    <Button 
                      variant="outline"
                      onClick={() => window.open(promoPage.website_url, '_blank')}
                    >
                      <Globe className="w-4 h-4 mr-2" />
                      Website
                    </Button>
                  )}
                </div>
              </div>

              {/* Social Links */}
              {promoPage.social_links && Object.keys(promoPage.social_links).length > 0 && (
                <div className="flex md:flex-col gap-2">
                  {Object.entries(promoPage.social_links).map(([platform, url]) => (
                    url && (
                      <Button 
                        key={platform}
                        variant="ghost" 
                        size="icon"
                        onClick={() => window.open(url, '_blank')}
                        title={platform}
                      >
                        {renderSocialIcon(platform)}
                      </Button>
                    )
                  ))}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* About */}
            <Card>
              <CardContent className="p-6">
                <h3 className="text-lg font-heading font-medium mb-4">About</h3>
                <p className="text-muted-foreground whitespace-pre-line">
                  {promoPage.description}
                </p>
              </CardContent>
            </Card>

            {/* Video (Premium Only) */}
            {isPremium && promoPage.video_url && (
              <Card>
                <CardContent className="p-6">
                  <h3 className="text-lg font-heading font-medium mb-4 flex items-center gap-2">
                    <Play className="w-5 h-5" />
                    Featured Video
                  </h3>
                  <div className="aspect-video bg-muted rounded-lg overflow-hidden">
                    {showVideo ? (
                      <iframe
                        src={promoPage.video_url.replace('watch?v=', 'embed/')}
                        className="w-full h-full"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    ) : (
                      <button 
                        onClick={() => setShowVideo(true)}
                        className="w-full h-full flex items-center justify-center bg-gradient-to-br from-primary/20 to-primary/5 hover:from-primary/30 hover:to-primary/10 transition-colors"
                      >
                        <div className="w-20 h-20 rounded-full bg-primary flex items-center justify-center shadow-lg">
                          <Play className="w-8 h-8 text-white ml-1" />
                        </div>
                      </button>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Services */}
            {promoPage.services?.length > 0 && (
              <Card>
                <CardContent className="p-6">
                  <h3 className="text-lg font-heading font-medium mb-4 flex items-center gap-2">
                    <DollarSign className="w-5 h-5" />
                    Services & Products
                  </h3>
                  <div className="grid sm:grid-cols-2 gap-4">
                    {promoPage.services.map((service, idx) => (
                      <Card key={service.id || idx} className={service.is_featured ? 'ring-2 ring-primary' : ''}>
                        <CardContent className="p-4">
                          {service.image_url && (
                            <img 
                              src={service.image_url} 
                              alt={service.title}
                              className="w-full h-32 object-cover rounded-lg mb-3"
                            />
                          )}
                          <div className="flex items-start justify-between gap-2 mb-2">
                            <h4 className="font-medium">{service.title}</h4>
                            {service.is_featured && (
                              <Badge variant="secondary" className="text-xs">Featured</Badge>
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
                            {service.description}
                          </p>
                          {formatPrice(service.price, service.price_type) && (
                            <p className="font-medium text-primary">
                              {formatPrice(service.price, service.price_type)}
                            </p>
                          )}
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Testimonials (Premium Only) */}
            {isPremium && promoPage.testimonials?.length > 0 && (
              <Card>
                <CardContent className="p-6">
                  <h3 className="text-lg font-heading font-medium mb-4 flex items-center gap-2">
                    <Quote className="w-5 h-5" />
                    Testimonials
                  </h3>
                  <div className="space-y-4">
                    {promoPage.testimonials.map((testimonial, idx) => (
                      <Card key={testimonial.id || idx} className="bg-muted/50">
                        <CardContent className="p-4">
                          <div className="flex items-start gap-4">
                            {testimonial.image_url ? (
                              <img 
                                src={testimonial.image_url} 
                                alt={testimonial.author_name}
                                className="w-12 h-12 rounded-full object-cover"
                              />
                            ) : (
                              <Avatar>
                                <AvatarFallback>
                                  {testimonial.author_name?.charAt(0)}
                                </AvatarFallback>
                              </Avatar>
                            )}
                            <div className="flex-1">
                              <p className="text-muted-foreground italic mb-3">
                                "{testimonial.content}"
                              </p>
                              <div className="flex items-center justify-between">
                                <div>
                                  <p className="font-medium">{testimonial.author_name}</p>
                                  {testimonial.author_title && (
                                    <p className="text-sm text-muted-foreground">
                                      {testimonial.author_title}
                                    </p>
                                  )}
                                </div>
                                {testimonial.rating && (
                                  <div className="flex items-center gap-1">
                                    {[...Array(5)].map((_, i) => (
                                      <Star 
                                        key={`star-${i}`}
                                        className={`w-4 h-4 ${
                                          i < testimonial.rating 
                                            ? 'fill-amber-400 text-amber-400' 
                                            : 'text-muted-foreground/30'
                                        }`}
                                      />
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Gallery (Premium Only) */}
            {isPremium && promoPage.gallery_images?.length > 0 && (
              <Card>
                <CardContent className="p-6">
                  <h3 className="text-lg font-heading font-medium mb-4 flex items-center gap-2">
                    <ImageIcon className="w-5 h-5" />
                    Gallery
                  </h3>
                  <div className="relative">
                    <div className="aspect-video bg-muted rounded-lg overflow-hidden">
                      <img 
                        src={promoPage.gallery_images[galleryIndex]}
                        alt={`Gallery ${galleryIndex + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    {promoPage.gallery_images.length > 1 && (
                      <>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="absolute left-2 top-1/2 -translate-y-1/2 bg-background/80"
                          onClick={() => setGalleryIndex((prev) => 
                            prev === 0 ? promoPage.gallery_images.length - 1 : prev - 1
                          )}
                        >
                          <ChevronLeft className="w-5 h-5" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="absolute right-2 top-1/2 -translate-y-1/2 bg-background/80"
                          onClick={() => setGalleryIndex((prev) => 
                            prev === promoPage.gallery_images.length - 1 ? 0 : prev + 1
                          )}
                        >
                          <ChevronRight className="w-5 h-5" />
                        </Button>
                        <div className="flex justify-center gap-2 mt-3">
                          {promoPage.gallery_images.map((_, idx) => (
                            <button
                              key={idx}
                              className={`w-2 h-2 rounded-full transition-colors ${
                                idx === galleryIndex ? 'bg-primary' : 'bg-muted-foreground/30'
                              }`}
                              onClick={() => setGalleryIndex(idx)}
                            />
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Quick Contact Card */}
            <Card className="sticky top-4">
              <CardContent className="p-6">
                <h3 className="font-heading font-medium mb-4">Contact Information</h3>
                <div className="space-y-3">
                  {promoPage.contact_email && (
                    <a 
                      href={`mailto:${promoPage.contact_email}`}
                      className="flex items-center gap-3 text-sm hover:text-primary transition-colors"
                    >
                      <Mail className="w-4 h-4 text-muted-foreground" />
                      {promoPage.contact_email}
                    </a>
                  )}
                  {promoPage.contact_phone && (
                    <a 
                      href={`tel:${promoPage.contact_phone}`}
                      className="flex items-center gap-3 text-sm hover:text-primary transition-colors"
                    >
                      <Phone className="w-4 h-4 text-muted-foreground" />
                      {promoPage.contact_phone}
                    </a>
                  )}
                  {promoPage.website_url && (
                    <a 
                      href={promoPage.website_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-3 text-sm hover:text-primary transition-colors"
                    >
                      <Globe className="w-4 h-4 text-muted-foreground" />
                      Visit Website
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                </div>
                
                <div className="border-t mt-4 pt-4">
                  <Button className="w-full" asChild>
                    <a href={`mailto:${promoPage.contact_email || ''}`}>
                      <MessageCircle className="w-4 h-4 mr-2" />
                      Get in Touch
                    </a>
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Stats */}
            <Card>
              <CardContent className="p-6">
                <div className="text-center">
                  <p className="text-2xl font-heading font-medium">{promoPage.view_count}</p>
                  <p className="text-sm text-muted-foreground">Profile Views</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MemberPromoPage;
